/**
 * Inline SVG icons in lucide-react style — keeps the widget bundle self-contained
 * (no lucide-preact dependency). One <svg> per export, sized via CSS on the parent.
 */
import type { JSX } from 'preact';

type Props = JSX.SVGAttributes<SVGSVGElement> & { size?: number };

const base = (size: number): JSX.SVGAttributes<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': 2,
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round',
  'aria-hidden': 'true',
});

export function IconMessageCircle({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export function IconX({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function IconMaximize2({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

export function IconMinimize2({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

export function IconPlay({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest} fill="currentColor" stroke="none">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

export function IconSend({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

export function IconPaperclip({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

export function IconSmile({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  );
}

export function IconUser({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export function IconBot({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4" />
      <line x1="8" y1="16" x2="8" y2="16" />
      <line x1="16" y1="16" x2="16" y2="16" />
    </svg>
  );
}

export function IconGlobe({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

export function IconRotateCcw({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}

export function IconDownload({ size = 24, ...rest }: Props) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/** WeChat brand glyph — solid fill, no stroke. */
export function IconWeChat({ size = 16, ...rest }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      {...rest}
    >
      <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z" />
    </svg>
  );
}

/** Static QR-code placeholder identical to the reference component. */
export function PlaceholderQR({ size = 160 }: { size?: number }) {
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} aria-hidden="true">
      <rect width="200" height="200" fill="white" />
      <rect x="20" y="20" width="30" height="30" fill="black" />
      <rect x="60" y="20" width="10" height="10" fill="black" />
      <rect x="80" y="20" width="10" height="10" fill="black" />
      <rect x="100" y="20" width="10" height="10" fill="black" />
      <rect x="120" y="20" width="10" height="10" fill="black" />
      <rect x="150" y="20" width="30" height="30" fill="black" />
      <rect x="20" y="60" width="10" height="10" fill="black" />
      <rect x="40" y="60" width="10" height="10" fill="black" />
      <rect x="60" y="60" width="30" height="30" fill="black" />
      <rect x="100" y="60" width="20" height="20" fill="black" />
      <rect x="150" y="60" width="10" height="10" fill="black" />
      <rect x="170" y="60" width="10" height="10" fill="black" />
      <rect x="20" y="100" width="10" height="10" fill="black" />
      <rect x="40" y="100" width="20" height="20" fill="black" />
      <rect x="80" y="100" width="10" height="10" fill="black" />
      <rect x="100" y="100" width="10" height="10" fill="black" />
      <rect x="130" y="100" width="20" height="20" fill="black" />
      <rect x="170" y="100" width="10" height="10" fill="black" />
      <rect x="20" y="150" width="30" height="30" fill="black" />
      <rect x="60" y="150" width="10" height="10" fill="black" />
      <rect x="80" y="150" width="30" height="30" fill="black" />
      <rect x="120" y="150" width="10" height="10" fill="black" />
      <rect x="150" y="150" width="30" height="30" fill="black" />
      <rect x="30" y="30" width="10" height="10" fill="white" />
      <rect x="160" y="30" width="10" height="10" fill="white" />
      <rect x="30" y="160" width="10" height="10" fill="white" />
    </svg>
  );
}
